import Edit2 from '@rsuite/icons/Edit'
import TrashIcon from '@rsuite/icons/Trash'
import {isEmpty} from 'lodash'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import IconButton from 'rsuite/esm/IconButton'
import Pagination from 'rsuite/Pagination'
import Table from 'rsuite/Table'
import Tag from 'rsuite/Tag'
import Toggle from 'rsuite/Toggle'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DeleteModal from 'src/app/modules/common/components/deleteModal'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import {StateParamsModel} from 'src/app/modules/common/Model'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import * as abroadRepresentative from '..'
import {BulkAction} from '../redux/service'
import AddAbroadRepresentative from './AddAbroadRepresentative'

const Cell = Table.Cell

// Remittance contact persons by country (API domain AbroadRepresentative; frontend get-abroad-representative/list)
const AbroadRepresentativeComponent = () => {
  const dispatch = useDispatch()
  const {data, loading, bulkDone} = useSelector((state: any) => state.abroadRepresentative)
  const [params, setParams] = useState<StateParamsModel>({page: 1, limit: 10})
  const [checkedValues, setCheckedValues] = useState<Array<string>>([])
  const [editSelectedData, setEditSelectedData] = useState<any>()
  const [open, setOpen] = useState(false)
  const [alertOpen, setAlertOpen] = useState(false)

  const refresh = () => dispatch(abroadRepresentative.actions.getAbroadRepresentative(params))
  useEffect(() => {
    refresh()
  }, [params])

  useEffect(() => {
    if (bulkDone) {
      toast.success(`Abroad representative ${bulkDone}d successfully`)
      setCheckedValues([])
      dispatch(abroadRepresentative.actions.resetAbroadRepresentative())
      refresh()
    }
  }, [bulkDone])

  const bulk = (action: BulkAction, ids: string[] = checkedValues) =>
    isEmpty(ids) ? toast.error('No data selected') : dispatch(abroadRepresentative.actions.bulkAbroadRepresentative(action, ids))

  const rows = (data?.abroad_representatives ?? []).map((item: any, i: number) => ({
    ...item,
    sn: (params.page - 1) * params.limit + (i + 1),
    countryName: item?.country?.title,
    persons: (item?.representatives?.representatives ?? []).map((s: any) => s?.name).join(', '),
  }))

  const ActionCell = ({rowData, ...props}: any) => (
    <Cell {...props} className='link-group'>
      <CheckPermissions type='Edit'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Edit</Tooltip>}>
          <IconButton
            appearance='subtle'
            icon={<Edit2 />}
            onClick={() => {
              setEditSelectedData(rowData)
              setOpen(true)
            }}
          />
        </Whisper>
      </CheckPermissions>
      <CheckPermissions type='Delete'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Delete</Tooltip>}>
          <IconButton
            appearance='subtle'
            icon={<TrashIcon />}
            onClick={() => {
              setCheckedValues([rowData.id])
              setAlertOpen(true)
            }}
          />
        </Whisper>
      </CheckPermissions>
      <Whisper placement='top' trigger='hover' speaker={<Tooltip>Status</Tooltip>}>
        <Toggle size='sm' checked={rowData.status} onClick={() => bulk(rowData.status ? 'disable' : 'enable', [rowData.id])} />
      </Whisper>
    </Cell>
  )

  const columns = [
    {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
    {label: 'Country', dataKey: 'countryName', flexGrow: 1, cell: <Cell dataKey='countryName' />, sortable: true},
    {label: 'Contact Persons', dataKey: 'persons', flexGrow: 2, cell: <Cell dataKey='persons' />},
    {
      label: 'Status',
      dataKey: 'status',
      width: 85,
      cell: (
        <Cell>
          {(rowData) => (
            <Tag color={rowData?.status ? 'green' : 'red'} size='sm'>
              {rowData?.status ? 'Active' : 'Inactive'}
            </Tag>
          )}
        </Cell>
      ),
    },
    {label: 'Action', width: 150, align: 'center', cell: <ActionCell dataKey='id' />},
  ]

  return (
    <div className='shadow p-3 bg-white rounded'>
      <DesignComponent
        moduleName='Abroad Representative'
        params={params}
        setParams={setParams}
        handleRefresh={refresh}
        handleAddModal={() => {
          setEditSelectedData({})
          setOpen(true)
        }}
        handleAlertOpen={() => setAlertOpen(true)}
        enableMultiple={() => bulk('enable')}
        disableMultiple={() => bulk('disable')}
        exportShow={false}
        importShow={false}
        data={rows}
        loading={loading}
      />

      <div className='datatable'>
        <RSuiteTable
          onChecked={(values: Array<string>) => setCheckedValues(values)}
          showCheckbox={true}
          columns={columns}
          data={rows}
          checkedValues={checkedValues}
          showLoading={loading}
          handleSort={(dataKey: string, order?: any) => setParams({...params, orderBy: dataKey, order})}
        />
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          className='mt-5'
          maxButtons={5}
          size='sm'
          layout={['total', '-', 'limit', '|', 'pager', 'skip']}
          total={data?.meta?.total || 0}
          limitOptions={[10, 20, 50, 100]}
          limit={params.limit}
          activePage={params.page}
          onChangePage={(page) => setParams({...params, page})}
          onChangeLimit={(limit) => setParams({...params, limit})}
        />
      </div>

      {open && (
        <AddAbroadRepresentative
          open={open}
          handleClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false)
            refresh()
          }}
          editSelectedData={editSelectedData}
        />
      )}

      {alertOpen && (
        <DeleteModal
          handleClick={() => {
            bulk('delete')
            setAlertOpen(false)
          }}
          isOpen={alertOpen}
          handleClose={() => setAlertOpen(false)}
        />
      )}
    </div>
  )
}

export default AbroadRepresentativeComponent
